import { publicEnv } from "@/lib/env/public";
import { datadogSite } from "@/lib/observability/datadog_trace";
import { drainRumCalls } from "@/lib/observability/rum-calls";
import {
  redactPrivateReferrer,
  redactPrivateRumView,
} from "@/lib/observability/rum-privacy";

export type AppName = "compare-annuities" | "specialists" | "charm";

export interface InitAppRUMArgs {
  app: AppName;
  isPrivateUrl?: (url: string) => boolean;
}

// Substrings for filtering third-party errors in datadogRum's beforeSend.
const SUPPRESSED_GENERIC_SCRIPT_ERROR = "Script error";
const SUPPRESSED_FB_AUTOFILL_BRIDGE_STACK =
  "setContactAutofillValuesFromBridge";
const SUPPRESSED_FB_AUTOFILL_CALLBACK = "_AutofillCallbackHandler";
const SUPPRESSED_WKWEBVIEW_API_NO_RESPONSE =
  "WKWebView API client did not respond";
const SUPPRESSED_STATSIG_NETWORK_ERROR = "[Statsig] A networking error";
const SUPPRESSED_STACK_OVERFLOW_MESSAGE = "Maximum call stack";
const SUPPRESSED_BROWSER_EXTENSION_STACK = "chrome-extension://";
const SUPPRESSED_WEBKIT_BRIDGE_MESSAGE = "window.webkit.messageHandlers";
const SUPPRESSED_GTM_IOS_HANDLER_STACK = "setupIosCallbackHandler";
const SUPPRESSED_CLICK_TRACKER_MESSAGE = ".className.slice is not a function";
const SUPPRESSED_CLICK_TRACKER_STACK = "buildClickData";
const SUPPRESSED_CEFSHARP_OBJECT_NOT_FOUND = "Object Not Found Matching Id";

const RESIZE_OBSERVER_LOOP_MESSAGE =
  "ResizeObserver loop completed with undelivered notifications";

interface ResizeObserverLoopContext {
  url_path: string;
  viewport_w: number;
  viewport_h: number;
  device_pixel_ratio: number;
  visibility_state: DocumentVisibilityState;
  scroll_y: number;
  radix_popper_count: number;
  open_state_count: number;
  radix_focus_guard_count: number;
  radix_scroll_area_count: number;
  active_element_tag: string | null;
  active_element_role: string | null;
  active_element_radix_attr: string | null;
}

function captureResizeObserverLoopContextInner(): ResizeObserverLoopContext {
  const active = document.activeElement;
  return {
    url_path: window.location.pathname,
    viewport_w: window.innerWidth,
    viewport_h: window.innerHeight,
    device_pixel_ratio: window.devicePixelRatio,
    visibility_state: document.visibilityState,
    scroll_y: window.scrollY,
    radix_popper_count: document.querySelectorAll(
      "[data-radix-popper-content-wrapper]",
    ).length,
    open_state_count: document.querySelectorAll('[data-state="open"]').length,
    radix_focus_guard_count: document.querySelectorAll(
      "[data-radix-focus-guard]",
    ).length,
    radix_scroll_area_count: document.querySelectorAll(
      "[data-radix-scroll-area-viewport]",
    ).length,
    active_element_tag: active?.tagName ?? null,
    active_element_role: active?.getAttribute("role") ?? null,
    active_element_radix_attr:
      active?.getAttributeNames().find((n) => n.startsWith("data-radix")) ??
      null,
  };
}

function captureResizeObserverLoopContext():
  | ResizeObserverLoopContext
  | {
      capture_failed: true;
    } {
  try {
    return captureResizeObserverLoopContextInner();
  } catch {
    return { capture_failed: true };
  }
}

let initialized = false;
let initializing = false;
let trackingDisabled = false;
let rumClient:
  | typeof import("@datadog/browser-rum-slim").datadogRum
  | undefined;

export function setAppRUMTrackingDisabled(disabled: boolean): void {
  trackingDisabled = disabled;
  rumClient?.setTrackingConsent(disabled ? "not-granted" : "granted");
}

export function detectWebview(userAgent: string): "fb" | "ig" | "none" {
  if (/FBAN|FB4A|FBIOS|FBAV/.test(userAgent)) {
    return "fb";
  }
  if (/Instagram/.test(userAgent)) {
    return "ig";
  }
  return "none";
}

export async function initAppRUM(args: InitAppRUMArgs): Promise<void> {
  if (initialized) {
    return;
  }
  if (initializing) {
    return;
  }
  if (typeof window === "undefined") {
    return;
  }
  const isPrivateUrl = args.isPrivateUrl ?? (() => false);
  const isPrivatePage = () => isPrivateUrl(window.location.href);
  if (trackingDisabled || isPrivatePage()) {
    return;
  }

  const applicationId = publicEnv("DATADOG_APPLICATION_ID");
  const clientToken = publicEnv("DATADOG_CLIENT_TOKEN");
  const env = publicEnv("DATADOG_ENV");
  if (applicationId == null || clientToken == null || env == null) {
    return;
  }

  const isProd = env === "prod";
  const isAgentApp = args.app === "charm";
  initializing = true;

  // The agent app is where usage dashboards count people, so every session
  // is recorded. Consumer apps are sampled to bound RUM cost.
  const sessionSampleRate = isAgentApp ? 100 : isProd ? 50 : 20;
  const sessionReplaySampleRate = isAgentApp ? 100 : 0;

  try {
    const { datadogRum } = await import("@datadog/browser-rum-slim");

    if (initialized || trackingDisabled || isPrivatePage()) {
      return;
    }
    rumClient = datadogRum;

    datadogRum.init({
      applicationId,
      clientToken,
      site: datadogSite(),
      service: args.app,
      env,
      version: publicEnv("APP_VERSION"),
      sessionSampleRate,
      sessionReplaySampleRate,
      enableExperimentalFeatures: ["inp_subparts"],
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      beforeSend: (event) => {
        if (
          trackingDisabled ||
          isPrivateUrl(event.view.url) ||
          isPrivatePage()
        ) {
          if (!trackingDisabled) {
            setAppRUMTrackingDisabled(true);
          }
          if (event.type !== "view") {
            return false;
          }
          redactPrivateRumView(event);
          return true;
        }
        event.view.url = redactSensitiveParams(event.view.url);
        redactPrivateReferrer(event, isPrivateUrl);
        if (event.view.referrer != null) {
          event.view.referrer = redactSensitiveParams(event.view.referrer);
        }
        if (event.type === "error") {
          const message = event.error.message;
          const stack = event.error.stack ?? "";
          if (message.includes(SUPPRESSED_GENERIC_SCRIPT_ERROR)) {
            return false;
          }
          if (stack.includes(SUPPRESSED_FB_AUTOFILL_BRIDGE_STACK)) {
            return false;
          }
          if (message.includes(SUPPRESSED_FB_AUTOFILL_CALLBACK)) {
            return false;
          }
          if (message.includes(SUPPRESSED_WKWEBVIEW_API_NO_RESPONSE)) {
            return false;
          }
          if (message.includes(SUPPRESSED_STATSIG_NETWORK_ERROR)) {
            return false;
          }
          // React DevTools (chrome-extension://fmkadmapgofadopljbjfkapdkoienihi)
          // patches console.warn/error. When our DD RUM logger captures an
          // error and forwards to console.warn, the patched method re-enters
          // the logger, creating an infinite loop that throws RangeError:
          // Maximum call stack size exceeded. Drop these — they reflect a
          // user's installed extension, not a real defect in our code.
          // Genuine call-stack overflows from our own code do not have a
          // chrome-extension:// frame.
          if (
            message.includes(SUPPRESSED_STACK_OVERFLOW_MESSAGE) &&
            stack.includes(SUPPRESSED_BROWSER_EXTENSION_STACK)
          ) {
            return false;
          }
          // setupIosCallbackHandler is injected by a GTM tag that targets
          // an iOS WebView native bridge (window.webkit.messageHandlers).
          // On non-WebView browsers (or iOS Safari outside the embedding
          // app) the bridge is undefined and the call throws. The tag
          // itself is third-party; we can't add the guard at the source.
          if (
            message.includes(SUPPRESSED_WEBKIT_BRIDGE_MESSAGE) &&
            stack.includes(SUPPRESSED_GTM_IOS_HANDLER_STACK)
          ) {
            return false;
          }
          // The compare-annuities /quote page has another third-party
          // inline script that probes the same `window.webkit.messageHandlers`
          // bridge but without the GTM-handler frame (the stack only carries
          // minified function names at line 1 of the page bundle). Scope to
          // the compare-annuities app so a future real `webkit.messageHandlers`
          // usage in our charm / specialists code would still surface.
          if (
            args.app === "compare-annuities" &&
            message.includes(SUPPRESSED_WEBKIT_BRIDGE_MESSAGE)
          ) {
            return false;
          }
          // buildClickData lives in a third-party click tracker injected
          // at runtime (anonymous source). It blindly calls
          // event.target.className.slice — fine when className is a
          // string, but SVG elements expose it as SVGAnimatedString, which
          // has no .slice. The tracker swallows the error itself; we get
          // it only because the host page re-logs via console.error.
          if (
            message.includes(SUPPRESSED_CLICK_TRACKER_MESSAGE) &&
            stack.includes(SUPPRESSED_CLICK_TRACKER_STACK)
          ) {
            return false;
          }
          // Microsoft Outlook SafeLink / Bing scan URLs in emails using
          // CefSharp (a .NET Chromium wrapper). CefSharp's automation layer
          // throws this when it tries to update a DOM node that was already
          // removed. Not a real defect; these bursts account for ~99.5% of
          // all specialists RUM errors. No stack trace; no user impact.
          if (
            message.includes(SUPPRESSED_CEFSHARP_OBJECT_NOT_FOUND) &&
            args.app !== "charm"
          ) {
            return false;
          }
          // ResizeObserver loop fires with an empty stack. Don't suppress —
          // attach diagnostic context so we can pinpoint the culprit when
          // it happens. Errors stay grouped under the same issue ID; each
          // event carries our context under @context.resize_observer_loop.
          if (message.includes(RESIZE_OBSERVER_LOOP_MESSAGE)) {
            event.context = {
              ...(event.context ?? {}),
              resize_observer_loop: captureResizeObserverLoopContext(),
            };
          }
        }
        return true;
      },
    });
    datadogRum.setGlobalContextProperty(
      "webview",
      detectWebview(navigator.userAgent),
    );
    initialized = true;
    drainRumCalls(datadogRum);
  } catch (error) {
    console.error("Failed to initialize Datadog RUM", error);
  } finally {
    initializing = false;
  }
}

// Shared session tokens, and search terms that often hold a customer's name
// or email (the inbox keeps its search in `q`), never reach RUM.
const SENSITIVE_PARAMS = ["session", "sws", "q"];

export function redactSensitiveParams(url: string): string {
  try {
    const parsed = new URL(url);
    for (const param of SENSITIVE_PARAMS) {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, "[REDACTED]");
      }
    }
    return parsed.toString();
  } catch {
    return url.replace(/([?&](?:session|sws|q)=)[^&#]*/g, "$1[REDACTED]");
  }
}
